import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './PdfVorschau.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const THUMB_WIDTH = 88;

const CLOSE_DURATION = 220;

export default function PdfVorschau({ doc, onClose }) {
  const [numPages, setNumPages]       = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageWidth, setPageWidth]     = useState(700);
  const [closing, setClosing]         = useState(false);

  const bodyRef      = useRef(null);
  const containerRef = useRef(null);
  const pageRefs     = useRef([]);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, CLOSE_DURATION);
  }, [onClose]);

  // Closing-State zurücksetzen wenn ein neues Dokument geöffnet wird
  useEffect(() => {
    if (doc) setClosing(false);
  }, [doc]);

  // Escape-Taste
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [handleClose]);

  // Breite der Seiten dynamisch messen
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      setPageWidth(Math.min(w - 64, 800));
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Aktuelle Seite via IntersectionObserver verfolgen
  const ratioMap = useRef({});
  useEffect(() => {
    if (!numPages || !bodyRef.current) return;
    const refs = pageRefs.current.filter(Boolean);
    if (!refs.length) return;
    ratioMap.current = {};
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          const idx = pageRefs.current.indexOf(e.target);
          if (idx !== -1) ratioMap.current[idx] = e.intersectionRatio;
        });
        let bestIdx = 0, bestRatio = -1;
        Object.entries(ratioMap.current).forEach(([idx, ratio]) => {
          if (ratio > bestRatio) { bestRatio = ratio; bestIdx = Number(idx); }
        });
        if (bestRatio >= 0) setCurrentPage(bestIdx + 1);
      },
      { root: bodyRef.current, threshold: [0, 0.1, 0.3, 0.5, 0.7, 0.9, 1.0] }
    );
    refs.forEach(r => io.observe(r));
    return () => io.disconnect();
  }, [numPages]);

  const goToPage = useCallback((n) => {
    const ref = pageRefs.current[n - 1];
    if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const onDocLoad = useCallback(({ numPages: n }) => {
    setNumPages(n);
    pageRefs.current = new Array(n).fill(null);
    setCurrentPage(1);
  }, []);

  if (!doc) return null;

  const isPDF   = doc.type === 'application/pdf';
  const isImage = doc.type?.startsWith('image/');

  return (
    <div className={`pvw-overlay${closing ? ' pvw-overlay--closing' : ''}`} onClick={handleClose}>
      <div className={`pvw-modal${closing ? ' pvw-modal--closing' : ''}`} onClick={e => e.stopPropagation()}>

        {/* ── Header ─────────────────────────────── */}
        <div className="pvw-header">
          <div className="pvw-header-left">
            <svg className="pvw-doc-icon" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                    stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
              <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
              <line x1="9" y1="13" x2="15" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="9" y1="17" x2="13" y2="17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span className="pvw-filename">{doc.filename}</span>
          </div>
          <div className="pvw-header-right">
            <a
              href={doc.url}
              download={doc.filename}
              className="pvw-download-btn"
              onClick={e => e.stopPropagation()}
            >
              <svg viewBox="0 0 24 24" fill="none" width="15" height="15">
                <path d="M12 3v13M7 11l5 5 5-5" stroke="currentColor" strokeWidth="2.2"
                      strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 20h18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
              </svg>
              Herunterladen
            </a>
            <button className="pvw-close-btn" onClick={handleClose} title="Schließen">
              <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* ── Content: Sidebar + Body ─────────────── */}
        <div className="pvw-content-area">

          {/* Linke Thumbnail-Sidebar */}
          {isPDF && (
            <div className="pvw-sidebar">
              <Document file={doc.url} loading={null} error={null}>
                {numPages && Array.from({ length: numPages }, (_, i) => (
                  <div
                    key={i}
                    className={`pvw-thumb-item ${currentPage === i + 1 ? 'pvw-thumb-item--active' : ''}`}
                    onClick={() => goToPage(i + 1)}
                    title={`Seite ${i + 1}`}
                  >
                    <div className="pvw-thumb-card">
                      <Page
                        pageNumber={i + 1}
                        width={THUMB_WIDTH}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </div>
                    <span className="pvw-thumb-label">Seite {i + 1}</span>
                  </div>
                ))}
              </Document>
            </div>
          )}

          {/* Scrollbarer Hauptbereich */}
          <div className="pvw-body" ref={bodyRef}>
            <div className="pvw-pages-wrap" ref={containerRef}>

              {isPDF && (
                <Document
                  file={doc.url}
                  onLoadSuccess={onDocLoad}
                  loading={<PvwLoading />}
                  error={<PvwError />}
                >
                  {numPages && Array.from({ length: numPages }, (_, i) => (
                    <div
                      key={i}
                      className="pvw-page-item"
                      ref={el => { pageRefs.current[i] = el; }}
                    >
                      <span className="pvw-page-label">Seite {i + 1}</span>
                      <div className="pvw-page-card">
                        <Page
                          pageNumber={i + 1}
                          width={pageWidth}
                          renderTextLayer
                          renderAnnotationLayer
                        />
                      </div>
                    </div>
                  ))}
                </Document>
              )}

              {isImage && (
                <img src={doc.url} alt={doc.filename} className="pvw-image" />
              )}

              {!isPDF && !isImage && (
                <iframe
                  src={`https://docs.google.com/gview?url=${encodeURIComponent(doc.url)}&embedded=true`}
                  title={doc.filename}
                  className="pvw-iframe-fallback"
                />
              )}

            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

function PvwLoading() {
  return (
    <div className="pvw-state">
      <div className="pvw-spinner" />
      <span>Dokument wird geladen…</span>
    </div>
  );
}

function PvwError() {
  return (
    <div className="pvw-state pvw-state--error">
      <svg viewBox="0 0 24 24" fill="none" width="28" height="28">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
      <span>Dokument konnte nicht geladen werden.</span>
    </div>
  );
}
